import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listDeliveryAreas,
  createDeliveryArea,
  updateDeliveryArea,
  deleteDeliveryArea,
  reorderDeliveryAreas,
  type AdminDeliveryArea,
} from "@/lib/admin/delivery-areas.functions";
import {
  deliveryAreaUpsertSchema,
  type DeliveryAreaInput,
} from "@/lib/admin/delivery-areas.schemas";

export const Route = createFileRoute("/_admin/admin/delivery-areas")({
  component: DeliveryAreasAdminPage,
});

type FormState = {
  neighbourhood: string;
  area_group: string;
  notes: string;
  is_demo: boolean;
  is_active: boolean;
};

function emptyForm(): FormState {
  return {
    neighbourhood: "",
    area_group: "",
    notes: "",
    is_demo: false,
    is_active: false,
  };
}

function fromRow(a: AdminDeliveryArea): FormState {
  return {
    neighbourhood: a.neighbourhood,
    area_group: a.area_group,
    notes: a.notes,
    is_demo: a.is_demo,
    is_active: a.is_active,
  };
}


const REORDER_ERROR_MSG: Record<string, string> = {
  incomplete_id_list: "Reorder must include every area. Reloading the list.",
  duplicate_ids: "Reorder contained duplicate rows.",
  unknown_id: "Reorder referenced an unknown area.",
  empty_id_list: "Nothing to reorder.",
  forbidden: "You don't have permission to reorder areas.",
};

function DeliveryAreasAdminPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listDeliveryAreas);
  const createFn = useServerFn(createDeliveryArea);
  const updateFn = useServerFn(updateDeliveryArea);
  const deleteFn = useServerFn(deleteDeliveryArea);
  const reorderFn = useServerFn(reorderDeliveryAreas);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "delivery-areas"],
    queryFn: () => listFn(),
    staleTime: 10_000,
  });

  const [editing, setEditing] = useState<AdminDeliveryArea | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<AdminDeliveryArea | null>(null);

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing) setForm(fromRow(editing));
  }, [editing]);

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "delivery-areas"] }),
      queryClient.invalidateQueries({ queryKey: ["delivery-areas", "public"] }),
    ]);

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string | null; patch: DeliveryAreaInput }) => {
      if (payload.id === null) {
        return createFn({ data: payload.patch as never });
      }
      return updateFn({ data: { id: payload.id, patch: payload.patch } as never });
    },
    onSuccess: async () => {
      toast.success("Delivery area saved");
      setEditing(null);
      setErrors({});
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === "duplicate_area") {
        setErrors({ neighbourhood: "An area with this name already exists." });
        toast.error("An area with this name already exists.");
        return;
      }
      toast.error(`Couldn't save the delivery area: ${msg}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Delivery area deleted");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      toast.error("Couldn't delete the delivery area.");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderFn({ data: { ids } as never }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "delivery-areas"] });
      const previous = queryClient.getQueryData<AdminDeliveryArea[]>([
        "admin",
        "delivery-areas",
      ]);
      if (previous) {
        const map = new Map(previous.map((p) => [p.id, p]));
        const reordered = ids
          .map((id, i) => {
            const row = map.get(id);
            return row ? { ...row, sort_order: (i + 1) * 10 } : null;
          })
          .filter((r): r is AdminDeliveryArea => r !== null);
        queryClient.setQueryData(["admin", "delivery-areas"], reordered);
      }
      return { previous };
    },
    onError: async (err, _vars, ctx) => {
      console.error(err);
      if (ctx?.previous) {
        queryClient.setQueryData(["admin", "delivery-areas"], ctx.previous);
      }
      const code = err instanceof Error ? err.message : "reorder_failed";
      toast.error(REORDER_ERROR_MSG[code] ?? "Couldn't reorder areas.");
      await queryClient.invalidateQueries({ queryKey: ["admin", "delivery-areas"] });
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  const areas = useMemo(() => data ?? [], [data]);

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= areas.length) return;
    const ids = areas.map((a) => a.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved);
    reorderMutation.mutate(ids);
  };

  const handleSave = () => {
    const parsed = deliveryAreaUpsertSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    const id = editing && editing !== "new" ? editing.id : null;
    saveMutation.mutate({ id, patch: parsed.data });
  };

  if (isLoading) {
    return (
      <div>
        <AdminPageHeader
          title="Delivery Areas"
          description="Manage the neighbourhoods you deliver to."
        />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <AdminPageHeader title="Delivery Areas" />
        <Alert variant="destructive">
          <AlertDescription>
            Couldn't load delivery areas.{" "}
            <button className="underline" onClick={() => refetch()}>
              Retry
            </button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader
        title="Delivery Areas"
        description="Create, edit, reorder and activate neighbourhoods used by the homepage delivery-area checker."
        actions={<ImportButtons moduleId="delivery-areas" />}
      />

      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {areas.length} area{areas.length === 1 ? "" : "s"}
        </p>
        <Button onClick={() => setEditing("new")} className="gap-2">
          <Plus className="h-4 w-4" aria-hidden /> New area
        </Button>
      </div>

      {areas.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No delivery areas yet. Create the first one.
        </div>
      ) : (
        <div className="relative overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 w-24 whitespace-nowrap">Order</th>
                <th className="p-3 whitespace-nowrap">Neighbourhood</th>
                <th className="p-3 whitespace-nowrap">Group</th>
                <th className="p-3 whitespace-nowrap">Flags</th>
                <th className="p-3 whitespace-nowrap">Status</th>
                
                <th className="sticky right-0 z-10 p-3 w-32 text-right bg-muted border-l border-border whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {areas.map((a, i) => (
                <tr key={a.id} className="group border-t border-border transition-colors hover:bg-muted/50">
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => move(i, -1)}
                        disabled={i === 0 || reorderMutation.isPending}
                        aria-label="Move up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => move(i, 1)}
                        disabled={i === areas.length - 1 || reorderMutation.isPending}
                        aria-label="Move down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-foreground">{a.neighbourhood}</div>
                  </td>
                  <td className="p-3 text-muted-foreground">{a.area_group || "—"}</td>
                  <td className="p-3">
                    {a.is_demo ? <Badge variant="outline">Demo</Badge> : null}
                  </td>
                  <td className="p-3">
                    {a.is_active ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <Badge variant="outline">Hidden</Badge>
                    )}
                  </td>
                  <td className="sticky right-0 z-10 p-3 bg-card border-l border-border transition-colors group-hover:bg-muted">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditing(a)}
                        aria-label="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirmDelete(a)}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing === "new" ? "New delivery area" : "Edit delivery area"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <FieldRow label="Neighbourhood *" error={errors.neighbourhood}>
              <Input
                value={form.neighbourhood}
                onChange={(e) =>
                  setForm({ ...form, neighbourhood: e.target.value })
                }
                placeholder="e.g. Bandra"
              />
            </FieldRow>
            <FieldRow
              label="Group / zone"
              hint="Optional. Used to group areas (e.g. Western, South)."
              error={errors.area_group}
            >
              <Input
                value={form.area_group}
                onChange={(e) => setForm({ ...form, area_group: e.target.value })}
              />
            </FieldRow>
            <FieldRow label="Notes" hint="Internal notes." error={errors.notes}>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </FieldRow>
            <div className="flex items-center gap-3">
              <Switch
                id="da_is_demo"
                checked={form.is_demo}
                onCheckedChange={(v) => setForm({ ...form, is_demo: v })}
              />
              <Label htmlFor="da_is_demo">Demo (never shown to customers)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="da_is_active"
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label htmlFor="da_is_active">
                Active (available on the public delivery-area checker)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this delivery area?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes “{confirmDelete?.neighbourhood}” permanently. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FieldRow({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="mb-1 block">{label}</Label>
      {children}
      {hint && !error ? (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      ) : null}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}